/**
 * Bulk Upload Validation Utilities
 * Provides validation and sanitization functions for Information Manager bulk upload
 */

// Valid priority values (case-insensitive)
const VALID_PRIORITIES = ['high', 'medium', 'low'];

// Valid type values (case-insensitive)
const VALID_TYPES = ['text', 'number', 'fileupload', 'link', 'fileuploadtolink', 'multiselect', 'choice'];

// Error message templates
const PRIORITY_ERROR_MESSAGE = `Invalid priority. Must be one of: ${VALID_PRIORITIES.join(', ')}`;
const TYPE_ERROR_MESSAGE = `Invalid type. Must be one of: ${VALID_TYPES.join(', ')}`;

/**
 * Sanitize and validate priority field
 * @param {string} priority - Raw priority value from upload
 * @returns {Object} { value: sanitizedValue, error: errorMessage }
 */
export function sanitizePriority(priority) {
  if (!priority || priority.toString().trim() === '') {
    return { value: '', error: PRIORITY_ERROR_MESSAGE };
  }

  const sanitized = priority.toString().trim().toLowerCase();
  
  if (VALID_PRIORITIES.includes(sanitized)) {
    // Return with proper casing: High, Medium, Low
    return { 
      value: sanitized.charAt(0).toUpperCase() + sanitized.slice(1), 
      error: null 
    };
  }

  return { 
    value: priority.toString().trim(), 
    error: PRIORITY_ERROR_MESSAGE 
  };
}

/**
 * Sanitize and validate type field
 * @param {string} type - Raw type value from upload
 * @returns {Object} { value: sanitizedValue, error: errorMessage }
 */
export function sanitizeType(type) {
  if (!type || type.toString().trim() === '') {
    return { value: '', error: TYPE_ERROR_MESSAGE };
  }

  const sanitized = type.toString().trim().toLowerCase();
  
  if (VALID_TYPES.includes(sanitized)) {
    return { 
      value: sanitized, 
      error: null 
    };
  }

  return { 
    value: type.toString().trim(), 
    error: TYPE_ERROR_MESSAGE 
  };
}

/**
 * Trim string input safely
 * @param {any} value - Input value to trim
 * @returns {string} Trimmed string value
 */
export function trimInput(value) {
  if (value === null || value === undefined) {
    return '';
  }
  return value.toString().trim();
}

/**
 * Parse and sanitize checklist references
 * @param {string} checklistRef - Comma-separated checklist references
 * @returns {Array} Array of trimmed references
 */
export function sanitizeChecklistRef(checklistRef) {
  if (!checklistRef) return [];
  return checklistRef
    .toString()
    .split(',')
    .map(x => x.trim())
    .filter(x => x !== '');
}
